import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@/utils/QueryKeys';
import { plato_api_ai_grading_path } from '@/modules/routes';
import useFlags from '@/hooks/useFlags';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { IQuestion } from '@/components/staffer/Grading/types';

export interface AIGradingEligibility {
  answered_count: number;
  eligible_count: number;
  writing_question_count: number;
}

// Grade By Student scopes AI grading to the shown student's writing questions; Grade By Question
// scopes it to the shown writing question for every student in the classroom.
const useScope = () => {
  const {
    assignment,
    classroom,
    isQuestionMode,
    questionId,
    student,
  } = useGradingStore();

  const assignmentQuestions: IQuestion[] = assignment?.questions || [];
  const questions = assignmentQuestions.filter(question => (
    question.type === 'OpenEndedQuestion' && (!isQuestionMode || question.id === questionId)
  ));
  const studentIds = isQuestionMode
    ? (classroom?.students || []).map(classroomStudent => classroomStudent.id)
    : [student?.id].filter(Boolean);

  return { question_ids: questions.map(question => question.id), student_ids: studentIds };
};

const useAIGrading = () => {
  const { aiGradingActive } = useFlags();
  const { isTeacher } = useGradingStore();
  const queryClient = useQueryClient();
  const params = useScope();

  const eligibilityQuery = useQuery({
    enabled: !!aiGradingActive && isTeacher && params.question_ids.length > 0 && params.student_ids.length > 0,
    queryFn: () => (
      axios.get<AIGradingEligibility>(plato_api_ai_grading_path(params)).then(res => res.data)
    ),
    queryKey: [QueryKeys.AIGradingEligibility, params.question_ids, params.student_ids],
  });

  const startMutation = useMutation({
    mutationFn: () => axios.post(plato_api_ai_grading_path(), params),
    // AIDEV-NOTE: refetching the answers is what lets the grading page see the new `generating` rows
    // and start polling — without it the banner and suggestions only appear after a reload.
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AIGradingEligibility] }),
      queryClient.invalidateQueries({ queryKey: ['studentAnswer'] }),
    ]),
  });

  return { eligibility: eligibilityQuery.data, startMutation };
};

export default useAIGrading;
